import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useU2000ExportExcelNotification } from "../../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { extractBaseLanguage } from "../../../u2000Core/tools/U2000LanguageUtils";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import U3125ArAgingInlineSpinner from "../../../u3125Facture/components/report/U3125ArAgingInlineSpinner";
import U3125ForfaitWizardInvoiceDateInput from "../../../u3125Facture/components/wizard/U3125ForfaitWizardInvoiceDateInput";
import { u3125ForfaitWizardTodayIsoDate } from "../../../u3125Facture/U3125Service";
import type { U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";
import { NS } from "../../U3134";
import {
  doExportTecAgeExcel,
  doExportTecAgePdf,
  luHandleAppiError,
  u3134BuildTecAgeRequestBody,
  u3134FilterTecAgeCustomerDetailByBucket,
  u3134FilterTecAgeSummaryByBucket,
} from "../../U3134Service";
import { useU3134TecAgeExportHandlers } from "../../U3134UseTecAgeExportHandlers";
import {
  u3134ClearTecAgeFullReportFallbackCache,
  useU3134TecAgeSummaryHandlers,
} from "../../U3134UseTecAgeSummaryHandlers";
import {
  u3134ClearTecAgeCustomerDetailCache,
  useU3134TecAgeCustomerDetailHandlers,
} from "../../U3134UseTecAgeCustomerDetailHandlers";
import U3134TecAgeBucketTable from "./U3134TecAgeBucketTable";
import U3134TecAgeHierarchySection from "./U3134TecAgeHierarchySection";
import U3134TecAgeReportToolbar from "./U3134TecAgeReportToolbar";

const AS_OF_DEBOUNCE_MS = 300;

const U3134TecAgeReportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const axios = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { summary, isLoading: isSummaryLoading, loadSummary } = useU3134TecAgeSummaryHandlers();
  const {
    customerDetail,
    loadingCustomerId,
    loadCustomerDetail,
    clearCustomerDetail,
  } = useU3134TecAgeCustomerDetailHandlers();
  const [asOfDate, setAsOfDate] = useState(() => u3125ForfaitWizardTodayIsoDate());
  const [selectedBucket, setSelectedBucket] = useState<U2000AgingBucket | null>(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoadRef = useRef(true);

  useU2000ExportExcelNotification();

  useEffect(() => {
    setLogicalUnitBreadcrumb([
      { label: t("title", { ns: NS }), href: "/U3134" },
      { label: t("tecAgeBreadcrumb", { ns: NS }) },
    ]);
    return () => setLogicalUnitBreadcrumb([]);
  }, [setLogicalUnitBreadcrumb, t]);

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const { exportExcel, exportPdf, isExportingExcel, isExportingPdf } = useU3134TecAgeExportHandlers({
    doExportTecAgeExcelFn: (body) => doExportTecAgeExcel(axios, body),
    doExportTecAgePdfFn: (body) => doExportTecAgePdf(axios, body),
    onExcelSuccess: () => toast.info(t("msgTecAgeReportGenerationStarted", { ns: NS })),
    onPdfSuccess: () => toast.info(t("msgTecAgeReportGenerationStarted", { ns: NS })),
    onError: ({ error }) => handleApiError(error),
  });

  const resetCustomerExpansion = useCallback(() => {
    setExpandedCustomerId(null);
    clearCustomerDetail();
    u3134ClearTecAgeCustomerDetailCache();
  }, [clearCustomerDetail]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = isFirstLoadRef.current ? 0 : AS_OF_DEBOUNCE_MS;
    isFirstLoadRef.current = false;
    debounceRef.current = setTimeout(() => {
      resetCustomerExpansion();
      setSelectedBucket(null);
      u3134ClearTecAgeFullReportFallbackCache();
      void loadSummary(asOfDate, null);
    }, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [asOfDate, loadSummary, resetCustomerExpansion]);

  useEffect(() => {
    if (selectedBucket != null) {
      resetCustomerExpansion();
    }
  }, [selectedBucket, resetCustomerExpansion]);

  const handleSelectBucket = useCallback((bucket: U2000AgingBucket | null) => {
    setSelectedBucket(bucket);
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!summary) return [];
    if (!selectedBucket) return summary.customers;
    return u3134FilterTecAgeSummaryByBucket(summary.customers, selectedBucket);
  }, [summary, selectedBucket]);

  const displayCustomerDetail = useMemo(() => {
    if (!customerDetail || customerDetail.customer.id !== expandedCustomerId) return null;
    if (!selectedBucket) return customerDetail;
    return u3134FilterTecAgeCustomerDetailByBucket(customerDetail, selectedBucket);
  }, [customerDetail, expandedCustomerId, selectedBucket]);

  const handleToggleCustomer = useCallback(
    (customerId: string) => {
      if (expandedCustomerId === customerId) {
        resetCustomerExpansion();
        return;
      }
      setExpandedCustomerId(customerId);
      clearCustomerDetail();
      void loadCustomerDetail(asOfDate, customerId, null);
    },
    [asOfDate, clearCustomerDetail, expandedCustomerId, loadCustomerDetail, resetCustomerExpansion],
  );

  const orgExportBody = useMemo(
    () =>
      u3134BuildTecAgeRequestBody({
        asOfDate,
        lang: extractBaseLanguage(i18n.language),
        bucket: selectedBucket,
      }),
    [asOfDate, i18n.language, selectedBucket],
  );

  const handleExportExcel = useCallback(() => {
    void exportExcel(orgExportBody);
  }, [exportExcel, orgExportBody]);

  const handleExportPdfHierarchy = useCallback(() => {
    void exportPdf(orgExportBody);
  }, [exportPdf, orgExportBody]);

  const handleExportPdfWipList = useCallback(() => {
    void exportPdf({ ...orgExportBody, pdfLayout: "wip_project_list" });
  }, [exportPdf, orgExportBody]);

  const hasReportData = summary != null;
  const isEmptyReport =
    hasReportData && summary.customers.length === 0 && summary.organization.totals.lineCount === 0;
  const isEmptyBucketFilter = hasReportData && selectedBucket != null && filteredCustomers.length === 0;

  const selectedBucketLabel =
    selectedBucket != null
      ? t(`tecAge.bucket.${selectedBucket}`, { ns: NS, defaultValue: selectedBucket })
      : "";

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{t("titleTecAgeReport", { ns: NS })}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("lblTecAgeAsOf", { ns: NS, date: asOfDate })}</p>
        </div>
        <U3134TecAgeReportToolbar
          onExportExcel={handleExportExcel}
          onExportPdfHierarchy={handleExportPdfHierarchy}
          onExportPdfWipList={handleExportPdfWipList}
          exportDisabled={!hasReportData || isSummaryLoading}
          isExportingExcel={isExportingExcel}
          isExportingPdf={isExportingPdf}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <U3125ForfaitWizardInvoiceDateInput
          value={asOfDate}
          onChange={setAsOfDate}
          label={t("lblTecAgeAsOfDate", { ns: NS })}
          disabled={isSummaryLoading}
        />
        {isSummaryLoading ? (
          <span className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
            <span>{t("msgTecAgeLoading", { ns: NS })}</span>
            <U3125ArAgingInlineSpinner />
          </span>
        ) : null}
      </div>

      {isEmptyReport ? (
        <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-muted-foreground">
          {t("msgTecAgeNoData", { ns: NS })}
        </p>
      ) : null}

      {hasReportData && !isEmptyReport ? (
        <>
          {selectedBucket ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm">
              <span>{t("lblTecAgeFilterActiveBucket", { ns: NS, bucket: selectedBucketLabel })}</span>
              <button
                type="button"
                className="text-primary-700 underline hover:no-underline"
                onClick={() => handleSelectBucket(null)}
              >
                {t("btnTecAgeClearBucketFilter", { ns: NS })}
              </button>
            </div>
          ) : null}

          <section className="space-y-2 rounded-md border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-semibold">{t("lblTecAgeOrganization", { ns: NS })}</h2>
            <U3134TecAgeBucketTable
              buckets={summary.organization.buckets}
              totals={summary.organization.totals}
              selectedBucket={selectedBucket}
              linkableBuckets
              onSelectBucket={handleSelectBucket}
            />
          </section>

          {isEmptyBucketFilter ? (
            <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-muted-foreground">
              {t("msgTecAgeNoDataForBucket", { ns: NS, bucket: selectedBucketLabel })}
            </p>
          ) : (
            <section className="rounded-md border border-gray-200 bg-white p-4">
              <U3134TecAgeHierarchySection
                customers={filteredCustomers}
                expandedCustomerId={expandedCustomerId}
                loadingCustomerId={loadingCustomerId}
                customerDetail={displayCustomerDetail}
                onToggleCustomer={handleToggleCustomer}
                selectedBucket={selectedBucket}
              />
            </section>
          )}
        </>
      ) : null}
    </div>
  );
};

export default U3134TecAgeReportPage;
